import EventEmitter from 'events'

import {
  Invitation,
  Inviter,
  Notification,
  Registerer,
  RegistererState,
  Session,
  SessionInviteOptions,
  SessionState,
  Subscriber,
  SubscriptionState,
  UserAgent,
  UserAgentDelegate,
  UserAgentOptions,
} from '@mangovoice/sip'
import {
  IncomingResponse,
  OutgoingInfoRequest,
  OutgoingInviteRequest,
  OutgoingRequestDelegate,
} from '@mangovoice/sip/core'
import { SessionDescriptionHandler } from '@mangovoice/sip/platform/web'
import { SipConnectionInfo } from '@mangovoice/types'
import { Analytics, Logger } from '@mangovoice/types'

import { buildEnvironment } from './common'
import { isClickToCallName } from './functions'
import { getPresenceStateFromNotify, PresenceState } from './xml'

const WSS_PORT = '7443'
const DTMF_DURATION = 250

export interface SessionStateChange {
  callUuid: string
  state: SessionState
}

export interface PresenceStateChange {
  subscribedNumber: string
  state: PresenceState
}

interface SipClientOptions {
  sipConnectionInfo: SipConnectionInfo
  userAgentString: string
  logger?: Logger
  analytics?: Analytics
}

export class SipClient
  extends EventEmitter
  implements UserAgentDelegate, OutgoingRequestDelegate
{
  private _ua: UserAgent
  private _sessions = new Map<string, Session>()
  private _sessionStates = new Map<string, SessionState>()
  private _subscriptions = new Map<string, Subscriber>()
  private _sipDomain: string
  private _logger?: Logger
  private _analytics?: Analytics
  private _registerer?: Registerer
  private _isDisposing = false

  constructor(options: SipClientOptions) {
    super()
    const { sipConnectionInfo, userAgentString, logger, analytics } = options

    if (logger) this._logger = logger
    if (analytics) this._analytics = analytics
    this._sipDomain = sipConnectionInfo.domain
    this._ua = new UserAgent(
      this.configureUserAgent(sipConnectionInfo, userAgentString),
    )
    this._ua.delegate = this
    this._analytics?.track('wss_request')
    this._ua
      .start()
      .then(() => {
        this._analytics?.track('wss_success')
      })
      .catch((e) => {
        this._analytics?.track('wss_conn_failure')
        this._logger?.error('Failed to start UserAgent', e as Error)
      })
  }

  /**
   * Tears down Sessions, Transport, and UA
   * @returns
   */
  public dispose(): Promise<void> {
    this._isDisposing = true
    this._logger?.info('Disposing SipClient')
    this._analytics?.track('unreg_request')
    try {
      this._analytics?.track('unreg_success')
      return this._ua.stop()
    } catch (error: unknown) {
      this._analytics?.track('unreg_failure')
      this._logger?.error('Failed to dispose SipClient', error as Error)
      this._isDisposing = false
      return Promise.reject(error)
    }
  }

  /**
   * Randomly choose a SIP server to provide basic load balancing
   * @param sipConnection
   * @returns
   */
  private chooseRandomServer = (sipConnection: SipConnectionInfo): string => {
    const servers = sipConnection.registration_servers
    const randomServer = servers[Math.floor(Math.random() * servers.length)]
    return `wss://${randomServer.sip_host}:${WSS_PORT}`
  }

  private configureUserAgent = (
    sipConnectionInfo: SipConnectionInfo,
    userAgentString: string,
  ): UserAgentOptions => {
    const isProduction = buildEnvironment === 'production'
    const sipUri = `sip:${sipConnectionInfo.username}@${sipConnectionInfo.domain}`
    const uri = UserAgent.makeURI(sipUri)
    if (!uri) {
      throw new Error('Failed to create URI during registration.')
    }
    return {
      uri,
      authorizationUsername: sipConnectionInfo.username,
      authorizationPassword: sipConnectionInfo.password,
      logConnector: (level, category, label, content) => {
        const logMessage = `${category}${label ?? ''} ${content}`
        switch (level) {
          case 'debug':
            this._logger?.debug(logMessage)
            break
          case 'log':
            this._logger?.info(logMessage)
            break
          case 'warn':
            this._logger?.warn(logMessage)
            break
          case 'error':
            this._logger?.warn(logMessage)
            break
        }
      },
      noAnswerTimeout: 60000,
      userAgentString,
      transportOptions: {
        server: this.chooseRandomServer(sipConnectionInfo),
        connectionTimeout: 30, // Increased from 10 to 30 seconds
        keepAliveInterval: 15, // Decreased from 29 to 15 seconds
        keepAliveDebounce: 50, // Keep this the same
        traceSip: isProduction ? false : true,
        sendDtmf: true,
      },
    }
  }

  private registrationStateListener(newState: RegistererState): void {
    this._analytics?.track(`reg_state_ch_${newState.toLowerCase()}`)
    this.emit('RegistrationStateChanged', newState)
  }

  private outboundSessionStateListener(
    callUuid: string,
  ): (newState: SessionState) => void {
    return (newState: SessionState): void => {
      const oldState = this._sessionStates.get(callUuid)
      this._logger?.info(
        `SessionStateChange Outbound: ${oldState} -> ${newState}`,
      )
      this._sessionStates.set(callUuid, newState)
      this._analytics?.track(`ob_sess_state_ch_${newState}`)
      this.emit('OutboundSessionStateChange', {
        callUuid,
        state: newState,
      } as SessionStateChange)
    }
  }

  private inboundSessionStateListener(
    callUuid: string,
  ): (newState: SessionState) => void {
    return (newState: SessionState): void => {
      const oldState = this._sessionStates.get(callUuid)
      this._logger?.info(
        `SessionStateChange Inbound: ${oldState} -> ${newState}`,
      )
      this._sessionStates.set(callUuid, newState)
      this._analytics?.track(`in_sess_state_ch_${newState}`)
      this.emit('InboundSessionStateChange', {
        callUuid,
        state: newState,
      } as SessionStateChange)
    }
  }

  private verifyUserAgent() {
    if (!this._ua.isConnected()) {
      throw new Error('UserAgent not connected.')
    }
  }

  private getSession(callUuid: string): Session {
    this.verifyUserAgent()
    const session = this._sessions.get(callUuid)
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!session) {
      throw new Error('Session not found.')
    }
    return session
  }

  private hasPeerConnection(callUuid: string): boolean {
    return !!this.getSdpHandler(callUuid).peerConnection
  }

  private getSdpHandler(callUuid: string): SessionDescriptionHandler {
    const session = this.getSession(callUuid)
    const sessionDescriptionHandler =
      session.sessionDescriptionHandler as SessionDescriptionHandler
    return sessionDescriptionHandler
  }

  private getPeerConnection(callUuid: string) {
    const sdpHandler = this.getSdpHandler(callUuid)
    if (!sdpHandler.peerConnection) {
      throw new Error('Peer connection not found.')
    }
    return sdpHandler.peerConnection
  }

  public subscribeToParking(parkTarget: string): void {
    try {
      this.verifyUserAgent()
    } catch {
      this._logger?.warn(
        'Failed to subscribe to parking lot: UserAgent not connected',
      )
      return
    }

    const existingSubscription = this._subscriptions.get(parkTarget)
    if (existingSubscription) {
      existingSubscription.subscribe()
      this._logger?.info(
        `SIP SUBSCRIBE: Resubscribed to parking lot ${parkTarget}`,
      )
      return
    }

    const uri = UserAgent.makeURI(`sip:park+${parkTarget}@${this._sipDomain}`)
    if (!uri) {
      throw new Error('Failed to create URI for park subscription')
    }

    const subscriber = new Subscriber(this._ua, uri, 'dialog')
    subscriber.delegate = {
      onNotify: (notification: Notification) => {
        notification.accept()

        const dialogInfo = notification.request.body
        const state = getPresenceStateFromNotify(dialogInfo)

        this.emit('ParkingStateChanged', {
          subscribedNumber: parkTarget,
          state: state,
        } as PresenceStateChange)
      },
    }
    subscriber.stateChange.addListener((newState: SubscriptionState) => {
      if (newState === SubscriptionState.Terminated) {
        this.emit('ParkingStateChanged', {
          subscribedNumber: parkTarget,
          state: PresenceState.Unknown,
        } as PresenceStateChange)
      }
    })
    subscriber.subscribe()
    this._logger?.info(`SIP SUBSCRIBE: Subscribed to parking lot ${parkTarget}`)
    this._subscriptions.set(parkTarget, subscriber)
  }

  public subscribeToExtension(extensionUuid: string): void {
    try {
      this.verifyUserAgent()
    } catch {
      this._logger?.warn(
        'Failed to subscribe to extension: UserAgent not connected',
      )
      return
    }

    const existingSubscription = this._subscriptions.get(extensionUuid)
    if (existingSubscription) {
      existingSubscription.subscribe()
      this._logger?.info(
        `SIP SUBSCRIBE: Resubscribed to extension ${extensionUuid}`,
      )
      return
    }

    const uri = UserAgent.makeURI(`sip:${extensionUuid}@${this._sipDomain}`)
    if (!uri) {
      throw new Error('Failed to create URI for extension subscription')
    }

    const subscriber = new Subscriber(this._ua, uri, 'dialog')
    subscriber.delegate = {
      onNotify: (notification: Notification) => {
        notification.accept()

        const dialogInfo = notification.request.body
        const state = getPresenceStateFromNotify(dialogInfo)

        this.emit('ExtensionStateChanged', {
          subscribedNumber: extensionUuid,
          state: state,
        } as PresenceStateChange)
      },
    }
    subscriber.stateChange.addListener((newState: SubscriptionState) => {
      if (newState === SubscriptionState.Terminated) {
        this.emit('ExtensionStateChanged', {
          subscribedNumber: extensionUuid,
          state: PresenceState.Unknown,
        } as PresenceStateChange)
      }
    })
    subscriber.subscribe()
    this._logger?.info(
      `SIP SUBSCRIBE: Subscribed to extension ${extensionUuid}`,
    )
    this._subscriptions.set(extensionUuid, subscriber)
  }

  /**
   * Get the audio stream coming from the connected call
   * @param callUuid
   * @returns
   */
  public getRemoteStream(callUuid: string): MediaStream {
    const remoteStream = new MediaStream()
    try {
      const peerConnection = this.getPeerConnection(callUuid)
      peerConnection.getReceivers().forEach((receiver) => {
        remoteStream.addTrack(receiver.track)
      })
      return remoteStream
    } catch {
      const session = this._sessions.get(callUuid)
      if (!session) {
        this._logger?.warn(
          'Failed to get inbound audio stream: session not found',
        )
        return remoteStream
      }
      if (session.state !== SessionState.Established) {
        this._logger?.warn(
          'Failed to get inbound audio stream: session not established',
        )
        return remoteStream
      }
      throw new Error('Failed to get inbound audio stream')
    }
  }

  /**
   * Replace the sending stream of the peer connection identified by callUuid
   * with a the provided stream
   * @param callUuid
   * @param stream
   */
  public setRemoteStream(callUuid: string, stream: MediaStream): void {
    try {
      const peerConnection = this.getPeerConnection(callUuid)
      peerConnection.getSenders()[0].track?.stop()
      peerConnection.getSenders()[0].replaceTrack(stream.getTracks()[0])
    } catch {
      const session = this._sessions.get(callUuid)
      if (!session) {
        this._logger?.warn(
          'Failed to set outbound audio stream: session not found',
        )
        return
      }
      if (session.state !== SessionState.Established) {
        this._logger?.warn(
          'Failed to set outbound audio stream: session not established',
        )
        return
      }
      throw new Error('Failed to set outbound audio stream')
    }
  }

  public isConnected() {
    return this._ua.isConnected()
  }

  public hasPendingInvitation(callUuid: string): boolean {
    const pendingInvitation = Array.from(this._sessions.values())
      .filter((session) => session instanceof Invitation)
      .find((invitation) => invitation.id.startsWith(callUuid))
    return pendingInvitation ? true : false
  }

  /**
   * Answer an inbound call
   * @param callUuid
   * @returns
   */
  public answerCall(callUuid: string): Promise<void> {
    const invitation = this.getSession(callUuid) as Invitation
    this._analytics?.track('answer_request')
    try {
      const req = invitation.accept()
      this._analytics?.track('answer_success')
      return req
    } catch (e: unknown) {
      this._analytics?.track('answer_failure')
      throw e
    }
  }

  /**
   * Reject an inbound call
   * @param callUuid
   * @returns
   */
  public rejectCall(callUuid: string): Promise<void> {
    const invitation = this.getSession(callUuid) as Invitation
    this._analytics?.track('reject_request')
    try {
      const req = invitation.reject()
      this._analytics?.track('reject_success')
      return req
    } catch (e: unknown) {
      this._analytics?.track('reject_failure')
      throw e
    }
  }

  /**
   * Starts a call to the target
   * @param callUuid
   * @param target
   * @returns
   */
  public async startCall(
    callUuid: string,
    target: string,
    displayName?: string,
    outboundCallerIdName?: string,
  ): Promise<OutgoingInviteRequest> {
    this.verifyUserAgent()

    this._logger?.info('sep credentials', {
      target,
      sipDomain: this._sipDomain,
    })
    const uri = UserAgent.makeURI(`sip:${target}@${this._sipDomain}`)
    if (!uri) {
      const err = new Error('Failed to create URI')
      this._logger?.error('Failed to create URI', err)
      throw err
    }
    const extraHeaders: string[] = []
    if (outboundCallerIdName) {
      extraHeaders.push('X-CALLER-ID: ' + outboundCallerIdName)
    }
    const inviter = new Inviter(this._ua, uri, {
      sessionDescriptionHandlerOptions: {
        constraints: {
          audio: true,
          video: false,
        },
      },
      extraHeaders,
    })
    if (displayName) {
      inviter.remoteIdentity.displayName = displayName
    }

    this._sessions.set(callUuid, inviter)
    this._sessionStates.set(callUuid, inviter.state)

    inviter.stateChange.addListener(
      this.outboundSessionStateListener(callUuid).bind(this),
    )
    this._logger?.info(`Sending INVITE to ${uri}`)

    this._analytics?.track('start_call_request')
    try {
      const req = await inviter.invite()
      this._analytics?.track('start_call_success')
      // eslint-disable-next-line @typescript-eslint/return-await
      return Promise.resolve(req)
    } catch (e: unknown) {
      this._analytics?.track('start_call_failure')
      throw e
    }
  }

  public async endCall(callUuid: string): Promise<void> {
    try {
      const session = this.getSession(callUuid)
      this.bye(session)
      this.endEstablishingSession(session)
    } catch (e: unknown) {
      this._logger?.warn('`endCall` caught an error', {
        callUuid,
        error: e,
      })
    }
  }

  public async rejectWithBusy(callUuid: string): Promise<void> {
    const invitation = this.getSession(callUuid) as Invitation
    this._analytics?.track('sess_reject_busy_request')
    try {
      await invitation.reject({ statusCode: 486 })
      this._analytics?.track('sess_reject_busy_success')
      return await Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track('sess_reject_busy_failure')
      throw e
    }
  }

  private async endEstablishingSession(session: Session): Promise<void> {
    if (session instanceof Inviter) {
      return this.cancelSession(session)
    } else if (session instanceof Invitation) {
      return this.reject(session)
    }
  }

  private async cancelSession(session: Inviter): Promise<void> {
    try {
      this._analytics?.track('sess_cancel_request')
      await session.cancel()
      this._logger?.info('TX CANCEL')
      this._analytics?.track('sess_cancel_success')
      return await Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track('sess_cancel_failure')
      throw e
    }
  }

  private async reject(session: Invitation): Promise<void> {
    try {
      this._analytics?.track('sess_reject_request')
      this._logger?.info('TX 486 Busy')
      await session.reject()
      this._analytics?.track('sess_reject_success')
      return await Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track('sess_reject_failure')
      throw e
    }
  }

  private async bye(session: Session): Promise<void> {
    try {
      this._analytics?.track('sess_bye_request')
      this._logger?.info('TX BYE')
      await session.bye()
      this._analytics?.track('sess_bye_success')
      return await Promise.resolve()
    } catch (e: unknown) {
      this._analytics?.track('sess_bye_failure')
      throw e
    }
  }

  /**
   * Places call on mute or unmutes call.
   * @param callUuid
   * @param muted
   * @returns
   */
  public muteCall(callUuid: string, muted: boolean): Promise<void> {
    const trackingEvent = muted ? 'mute' : 'unmute'
    try {
      if (!this.hasPeerConnection(callUuid)) {
        this._logger?.warn(`muteCall UUID=${callUuid} no peer connection`)
        return Promise.resolve()
      }

      const peerConnection = this.getPeerConnection(callUuid)
      this._analytics?.track(trackingEvent + '_request')
      peerConnection.getSenders().forEach((sender: RTCRtpSender) => {
        if (sender.track) {
          sender.track.enabled = !muted
        }
      })
      this._analytics?.track(trackingEvent + '_success')
    } catch (e: unknown) {
      this._analytics?.track(trackingEvent + '_failure')
      throw e
    }

    this._logger?.info(`muteCall UUID=${callUuid} Muted=${muted}`)

    return Promise.resolve()
  }

  /**
   * Places call on hold or unholds call.
   * @param callUuid
   * @param hold
   * @returns
   */
  public async holdCall(
    callUuid: string,
    hold: boolean,
  ): Promise<OutgoingInviteRequest> {
    const session = this.getSession(callUuid)

    type HoldModifier = (
      description: RTCSessionDescriptionInit,
    ) => Promise<RTCSessionDescriptionInit>

    const holdModifier: HoldModifier = (description) => {
      const sdp = description.sdp ?? ''
      const lines = sdp.split('\r\n')
      const newSdpLines = lines.map((line) => {
        if (hold) {
          if (line.startsWith('a=sendrecv')) return 'a=sendonly'
          if (line.startsWith('a=recvonly')) return 'a=sendonly'
        } else {
          if (line.startsWith('a=sendonly')) return 'a=sendrecv'
          if (line.startsWith('a=recvonly')) return 'a=sendrecv'
        }
        return line
      })
      description.sdp = newSdpLines.join('\r\n')
      return Promise.resolve(description)
    }

    const options: SessionInviteOptions = {
      sessionDescriptionHandlerModifiers: [holdModifier],
    }

    try {
      this._analytics?.track('hold_request')
      this._logger?.info(`holdCall UUID=${callUuid} Hold=${hold}`)
      const req = await session.invite(options)
      this._analytics?.track('hold_success')
      return req
    } catch (e: unknown) {
      this._logger?.error('hold invite failed', e as Error)
      this._analytics?.track('hold_failure')
      throw e
    }
  }

  /**
   * Blind transfer to target
   * @param callUuid
   * @param target
   * @returns
   */
  public async blindTransferCall(
    callUuid: string,
    target: string,
  ): Promise<OutgoingInviteRequest> {
    const session = this.getSession(callUuid)
    target = `sip:${target}@${this._sipDomain}`
    try {
      const uri = UserAgent.makeURI(target)
      if (!uri) {
        throw new Error('invalid target')
      }
      this._logger?.info(`transferCall UUID=${callUuid} target=${target}`)
      this._analytics?.track('transfer_request')
      const req = await session.refer(uri)
      this._analytics?.track('transfer_success')
      return req
    } catch (e: unknown) {
      // write to log and send to sentry
      this._logger?.error(`transfer call UUID=${callUuid} target=${target}`)
      this._analytics?.track('transfer_failure')
      throw e
    }
  }

  /**
   * Attended transfer to Established call
   * @param callUuid
   * @param targetCallUuid
   * @returns
   */
  public async attendedTransferCall(
    callUuid: string,
    targetCallUuid: string,
  ): Promise<OutgoingInviteRequest> {
    const session = this.getSession(callUuid)
    const targetSession = this.getSession(targetCallUuid)
    if (targetSession.state !== SessionState.Established) {
      throw new Error(
        `Attented transfer from ${callUuid} to unestablished session ${targetCallUuid}`,
      )
    }
    try {
      this._logger?.info(
        `attendedTransferCall UUID=${callUuid} targetUUID=${targetCallUuid}`,
      )
      this._analytics?.track('attended_transfer_request')
      const req = await session.refer(targetSession)
      this._analytics?.track('attended_transfer_success')
      return req
    } catch (e: unknown) {
      // write to log and send to sentry
      this._logger?.error(
        `attended transfer call UUID=${callUuid} targetUUID=${targetCallUuid}`,
      )
      this._analytics?.track('attended_transfer_failure')
      throw e
    }
  }

  /**
   * Passes DTMF digit to the session using SIP INFO
   * @param callUuid
   * @param digit
   * @returns
   */
  public async sendDtmf(
    callUuid: string,
    digit: string,
  ): Promise<OutgoingInfoRequest> {
    const session = this.getSession(callUuid)
    const options = {
      requestOptions: {
        body: {
          contentDisposition: 'render',
          contentType: 'application/dtmf-relay',
          content: `Signal=${digit}\r\nDuration=${DTMF_DURATION}`,
        },
      },
    }
    try {
      this._logger?.info(`sendDtmf UUID=${callUuid} digit=${digit}`)
      this._analytics?.track('dtmf_request')
      const req = await session.info(options)
      this._analytics?.track('dtmf_success')
      return req
    } catch (e: unknown) {
      this._analytics?.track('dtmf_failure')
      this._logger?.error('dtmf failed', e as Error)
      throw e
    }
  }

  /**
   * Introspects session to parse display name or user name from the remote identity
   * @param callUuid
   * @returns
   */
  public getRemoteCallerId(callUuid: string): {
    displayName: string
    friendlyName: string
    user: string | undefined
  } {
    const session = this.getSession(callUuid)
    const remoteIdentity = session.remoteIdentity
    return {
      displayName: remoteIdentity.displayName,
      friendlyName: remoteIdentity.friendlyName,
      user: remoteIdentity.uri.user,
    }
  }

  onInvite(invitation: Invitation): void {
    const callUuid = invitation.id.substring(0, 36)
    this._logger?.info(`onInvite UUID=${callUuid}`)
    this._analytics?.track('inv_received')

    const isClickToCall =
      invitation.request.getHeader('X-Click-To-Call')?.toLowerCase() ===
        'true' || isClickToCallName(invitation.remoteIdentity.displayName)

    this._sessions.set(callUuid, invitation)
    this._sessionStates.set(callUuid, invitation.state)

    this.emit('IncomingInviteReceived', {
      callUuid: callUuid,
      number: invitation.remoteIdentity.uri.user ?? 'Unknown Number',
      name: invitation.remoteIdentity.displayName,
      isClickToCall,
    })

    invitation.stateChange.addListener(
      this.inboundSessionStateListener(callUuid).bind(this),
    )
  }

  async onConnect(): Promise<void> {
    this._logger?.info('Transport Connected')
    this.emit('TransportConnected')

    // Create a new Registerer every time a connection is made
    const registerer = new Registerer(this._ua)
    this._registerer = registerer

    // Add the listener for state changes
    registerer.stateChange.addListener(
      this.registrationStateListener.bind(this),
    )

    try {
      this._analytics?.track('reg_request')
      this._logger?.info('Registration request')
      await registerer.register({ requestDelegate: this })
      this._analytics?.track('reg_success')
    } catch (e: Error | unknown) {
      this._analytics?.track('reg_failure')
      this._logger?.error('Registration failed', e as Error)
    }
  }

  onDisconnect(error?: Error | undefined): void {
    this._logger?.info('TransportDisconnected')
    try {
      if (
        !this._isDisposing &&
        this._registerer?.state === RegistererState.Registered
      ) {
        this._registerer.unregister()
        this._logger?.info('Unregister succeeded after TransportDisconnected')
      } else {
        this._logger?.info(
          `Unregister skipped after TransportDisconnected. isDisposing=${this._isDisposing} state=${this._registerer?.state}`,
        )
      }
    } catch {
      this._logger?.info('Unregister failed after TransportDisconnected')
    }
    this.emit('TransportDisconnected', error)
  }

  onReject(response: IncomingResponse): void {
    this._logger?.info('RegistrationRejected')
    this.emit('RegistrationRejected', response.message.reasonPhrase)
  }
}
